import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Background, BackgroundVariant, Panel, ReactFlow,
} from '@xyflow/react';
import { ReactComponent as AiIcon } from 'assets/icons/ai.svg';
import { clsx } from 'clsx';
import AIAgentsSidebar from 'components/AIAgentsSidebar/AIAgentsSidebar';
import AICardPanel from 'components/AICardPanel/AICardPanel';
import AICreatorBoMUnits from 'components/AICreatorBoMUnits/AICreatorBoMUnits';
import AICreatorSupplierUnits from 'components/AICreatorSupplierUnits/AICreatorSupplierUnits';
import AIGeneratingModal from 'components/AIGeneratingModal/AIGeneratingModal';
import AiSubScreen from 'components/AiSubScreen/AiSubScreen';
import AnalysisSidebar from 'components/AnalysisSidebar/AnalysisSidebar';
import BoardGroupBySwitch from 'components/BoardGroupBySwitch/BoardGroupBySwitch';
import Button from 'components/Button/Button';
import ConnectionVCModal from 'components/ConnectionVCModal/ConnectionVCModal';
import DeleteRestrictedModal from 'components/DeleteRestrictedModal/DeleteRestrictedModal';
import FiltersPanel from 'components/FiltersPanel/FiltersPanel';
import ImpactScenariosSidebar from 'components/ImpactScenariosSidebar/ImpactScenariosSidebar';
import MonitoringAgentHubSidebar from 'components/MonitoringAgent/MonitoringAgentHubSidebar/MonitoringAgentHubSidebar';
import MonitoringResultsPanel from 'components/MonitoringAgent/MonitoringResultsPanel/MonitoringResultsPanel';
import ScheduleConfigPanel from 'components/MonitoringAgent/ScheduleConfigPanel/ScheduleConfigPanel';
import ScheduleRunHistoryPanel from 'components/MonitoringAgent/ScheduleRunHistoryPanel/ScheduleRunHistoryPanel';
import { resolveMonitoringCardPanelExistingSession } from 'components/MonitoringAgent/utils/monitoringSessionUtils';
import SessionsListSidebar from 'components/SessionsListSidebar/SessionsListSidebar';
import { useAiSubScreenContext } from 'contexts/AiScreenContext';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';

import Loader from '../../../../../components/Loader/Loader';
import RenameModal from '../../../../../components/RenameModal/RenameModal';
import {
  REACT_FLOW,
  VALUE_CHAIN_MAP,
  VALUE_CHAIN_MAP_CONNECTION_COLORS,
} from '../../../../../constants/general';
import {
  BUTTON_VARIANT,
  DELETE_MODAL_TITLES,
  VALUE_CHAIN_MAP_CUSTOM_NODES,
  VALUE_CHAIN_MAP_MODALS,
} from '../../../../../constants/interfaces';
import ColorsTip from './components/ColorsTip';
import CustomBoardNode from './components/CustomBoardNode';
import CustomEdge from './components/CustomEdge';
import CustomGroupMoreNode from './components/CustomGroupMoreNode';
import CustomGroupNode from './components/CustomGroupNode';
import CustomMarkerEnd from './components/CustomMarkerEnd';
import CustomSwimlaneNode from './components/CustomSwimlaneNode';
import CustomUnitNode from './components/CustomUnitNode';
import RightPanel from './components/RightPanel';
import StickyColumnHeaders from './components/StickyColumnHeaders';
import { isMonitoringAgentKind } from './interfaces/TValueChainActiveAgentKind';

import type { IValueChainView } from './interfaces/IValueChainView';

import './sass/reactFlowStyles.scss';
import styles from './sass/ValueChainView.module.scss';

const nodeTypes = {
  [VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD]: CustomBoardNode,
  [VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE]: CustomSwimlaneNode,
  [VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT]: CustomUnitNode,
  [VALUE_CHAIN_MAP_CUSTOM_NODES.GROUP]: CustomGroupNode,
  [VALUE_CHAIN_MAP_CUSTOM_NODES.GROUP_MORE]: CustomGroupMoreNode,
};

const edgeTypes = {
  custom: CustomEdge,
};

const noop = () => {};

function ValueChainView({
  onMoveEnd, isInitialViewportReady, onConnect, onNodesChange, onEdgesChange, shouldShowLoader,
  isInitialLayoutPending, isBoardContentHidden, nodes, styledEdges, onNodeDrag,
  selectedBoard, closeModal, onRenameSubmit, activeModal, onDrop, onDragOver, onAddSwimlane, isFullScreenActive,
  onNodeDragStop, onNodeDragStart, isAccessDisabled, isDeleteModalOpen, setDeleteModalOpen,
  onDeleteSwimlaneConfirm, enumData, onAddServiceConnection, onEdgeDoubleClick, onEdgeClick,
  selectedEdge, openFilters, onOpenFilters, openImpactScenariosSidebar,
  onOpenImpactScenariosSidebar, onCloseImpactScenariosSidebar, selectedImpactAnalysis,
  onBackToImpactScenariosSidebar, impactScenarios,
  isImpactScenariosLoading, isImpactScenarioCreating, isImpactScenariosDeleting,
  isImpactScenarioUpdating, onCreateImpactScenario, onOpenImpactAnalysis,
  onRenameImpactAnalysis, boardEntities, isCreateScenarioSaving, onSaveScenario, onDeleteAnalysisScenario,
  onUpdateScenario, valueChainMapAnalysisById, isAnalysisDetailsLoading, valueChainMapAnalysisResults,
  isAnalysisResultsLoading, hasMoreAnalysisResults, valueChainMapAnalysisScenarios,
  isAnalysisScenariosLoading, hasMoreAnalysisScenarios, isCopyAnalysisResultLoading,
  isCopyAnalysisScenariosLoading, isDeleteAnalysisResultLoading, isStartImpactAnalysisLoading,
  onLoadAnalysisResults, onLoadMoreAnalysisResults, onLoadAnalysisScenarios,
  onLoadMoreAnalysisScenarios, onCopyAnalysisResult,
  onCopyAnalysisScenarios, onDeleteAnalysisResult, onStartImpactAnalysis, followedResultId, openRequestId,
  openResultId,
  activeScenarioId, onSelectScenario,
  activeViewScenarioId, onViewScenario,
  activeViewResultId, onViewResult,
  pendingScenarioInitialData, onClearPendingScenarioInitialData,
  onDeleteImpactScenario, openAIAgentsSidebar, openAISessionsSidebar, openMonitoringHubSidebar,
  activeAgentKind,
  onOpenAIAgentsSidebar,
  onCloseAIAgentsSidebar, onCloseAISessionsSidebar, openAICardPanel, onOpenSupplierDiscoveryPanel,
  onOpenReputationMonitoring, onOpenRegulationMonitoring, onOpenFinanceMonitoring,
  onOpenOwnershipMonitoring, onOpenClimateMonitoring,
  onBackToAIAgentsSidebar, onBackToAIAgentsFromMonitoringHub,
  onBackToMonitoringHubFromCardPanel, onStartMonitoringSession,
  onBackToAISessionsSidebar, onCreateAISessionSelection, onCreateMonitoringSessionSelection,
  isNewNTierSelection = false,
  isNewMonitoringSelection = false,
  onCreateMonitoringScheduleSelection, onContinueToSchedule, onBackFromScheduleConfig,
  onCloseScheduleConfigPanel, onSaveMonitoringSchedule, onAgentConfigIdChange,
  onNTierSubTypeChange,
  monitoringAgentConfigId, valueChainMapId, openScheduleConfigPanel,
  editingMonitoringSchedule, monitoringCardPanelFlow,
  monitoringHubActiveTab, onMonitoringHubActiveTabChange, monitoringSchedules,
  isMonitoringSchedulesLoading, isMonitoringSchedulesLoadFailed, isCreateMonitoringScheduleLoading,
  isDeleteMonitoringScheduleLoading, openScheduleRunHistoryPanel, activeMonitoringSchedule,
  onOpenMonitoringSchedule, onEditMonitoringSchedule, onDeleteMonitoringSchedule,
  onPauseMonitoringSchedule,
  onResumeMonitoringSchedule, onRunMonitoringScheduleNow,
  isRunMonitoringScheduleNowLoading,
  onBackFromScheduleRunHistory, onCloseScheduleRunHistoryPanel,
  onEditAISession, onEditMonitoringSession, onDeleteAISession, onDeleteMonitoringSession,
  onCloseAICardPanel, onAnalyzeBom, hasSupplierNTierAttention, hasReputationMonitoringAttention,
  hasRegulationMonitoringAttention, hasFinanceMonitoringAttention, hasOwnershipMonitoringAttention,
  hasClimateMonitoringAttention,
  monitoringAgentDefinition, monitoringAgentTitleKey, existingMonitoringSession,
  effectiveMonitoringSession,
  openMonitoringResultsPanel,
  monitoringResultsSessionTitle,
  monitoringResultsSessionId,
  monitoringResultsExecutionKey,
  monitoringResultsRunId,
  monitoringResultsBreadcrumbMode,
  monitoringResultsScheduleTitle,
  monitoringResultsRunLabel,
  monitoringResultsValueChainMapId,
  monitoringResultsFallbackEntities,
  showMonitoringImpactScenarioCta,
  isMonitoringImpactScenarioCreating,
  onCreateImpactScenarioFromMonitoringResults,
  onBackFromMonitoringResultsPanel,
  onCloseMonitoringResultsPanel,
  onViewMonitoringSessionResults,
  selectedAICards,
  aiSessions, monitoringSessions, isAISessionsLoading, isAISessionsLoadFailed, isAISessionActionLoading,
  aiGeneratingButtonHint, shouldShowAIGeneratingFloatingButton,
  isAIGeneratingHintVisible, isAIGeneratingModalOpen, isMonitoringGeneratingOverview = false,
  generatingMonitoringSessions = [],
  groupByType, onChangeGroupBy,
  onOpenAIGeneratingModal, onCloseAIGeneratingModal,
  openAICreatorBoMUnits, onCloseAICreatorBoMUnits,
  openAICreatorSupplierUnits, onCloseAICreatorSupplierUnits,
  onOpenGeneratingSession, bomUnitsCards,
  bomUnitsInitialTabNodeId, availableBoMManufacturerCards, bomUnitsSession,
  supplierUnitsCards, supplierUnitsInitialTabNodeId, supplierUnitsSession, agentSessionsFulfilledTimeStamp,
  refetchAgentSessions,
  generatingAICards, generatingAISessions, onBoMGenerateWithAI, onBoMAddRecord,
  onBoMAddManufacturer, onBoMConfirm, onBoMProcessingTabsChange,
  onSupplierGenerateWithAI, onSupplierAddRecord, onSupplierConfirm, onSupplierProcessingTabsChange,
  onSearchSuppliersSuccess, allAvailableAICards, onSyncSelectedAICardsFromSession,
  onRemoveSelectedAICard, onToggleSelectedAICard, onResetSelectedAICards,
  filtersData, orgStructureData, isSelectedFilter, onApplyFilters, districtErrors, onNodeDoubleClick,
  followConnections, onToggleFollowConnections,
  isDraftReviewMode, isDiscardDraftConfirmOpen, onOpenDiscardDraftConfirm, onRejectDiscardDraftConfirm,
  onConfirmDiscardDraft, isSupplierDiscoveryReview, onUseDraftResult, isUseDraftResultDisabled,
  draftCardToDeleteName, isDraftDeleteModalOpen, onConfirmDraftDelete, onRejectDraftDelete, isDraftDeleteLoading,
  selectedCurrentFilters, setSelectedCurrentFilters,
  sessionId, setSessionId, existingSession, isVCAgentSessionsLoaded, isVCAgentSessionsLoadFailed,
  isAIDraftUnitsReadyForSelectionSync, handleCloseModalBom, handleCloseModalSupplier,
  isBomSocketConnected, sendBomSocketUnitUpdated, lastBomSocketPayload,
  isSocketPollingFallbackActive, isScenarioViewMode, viewportController, isGroupingMode,
}: IValueChainView) {
  const { t } = useTranslation();
  // The tab the user switched to, over the map the route names. The side panels stack on it below.
  useAiSubScreenContext({ name: t('valueChainMap.valueChain') });

  let aiCardPanelExistingSession = existingSession;

  if (isMonitoringAgentKind(activeAgentKind)) {
    aiCardPanelExistingSession = resolveMonitoringCardPanelExistingSession({
      isNewMonitoringSelection,
      monitoringCardPanelFlow,
      existingMonitoringSession,
      sessionType: monitoringAgentDefinition.sessionType,
    });
  } else if (isNewNTierSelection) {
    // New selection must not rebind the active/legacy in-progress n-tier session.
    aiCardPanelExistingSession = undefined;
  }

  return (
    <>
      {shouldShowAIGeneratingFloatingButton ? (
        <Panel
          className={clsx(styles.aiGeneratingButtonPanel, {
            [styles.fullScreenTopControl]: isFullScreenActive,
          })}
          position="top-right"
        >
          {isAIGeneratingHintVisible ? (
            <div className={styles.aiGeneratingButtonHint} role="status" aria-live="polite">
              <span className={styles.aiGeneratingButtonHintText}>
                {aiGeneratingButtonHint}
              </span>
            </div>
          ) : null}
          <button
            type="button"
            className={styles.aiGeneratingButton}
            onClick={onOpenAIGeneratingModal}
            aria-label={t('valueChainMap.aiGeneratingModalTitle')}
            title={t('valueChainMap.aiGeneratingModalTitle')}
          >
            <AiIcon width={20} height={20} aria-hidden="true" />
          </button>
        </Panel>
      ) : null}
      {!isDraftReviewMode && !isScenarioViewMode ? (
        <Panel
          className={clsx(styles.groupByPanel, {
            [styles.fullScreen]: isFullScreenActive,
          })}
          position="top-left"
        >
          <BoardGroupBySwitch value={groupByType} onChange={onChangeGroupBy} borderless />
        </Panel>
      ) : null}
      {!isDraftReviewMode ? (
        <Panel
          className={clsx(styles.rightCenterPanel, {
            [styles.aloneElement]: isAccessDisabled,
          })}
          position="top-right"
        >
          <RightPanel
            isAccessDisabled={isAccessDisabled}
            onClickFilters={onOpenFilters}
            onClickScenarios={onOpenImpactScenariosSidebar}
            selectedFilter={isSelectedFilter}
            onClickAIPanel={onOpenAIAgentsSidebar}
            isScenarioViewMode={isScenarioViewMode}
          />
        </Panel>
      ) : null}
      <Panel
        position="bottom-left"
        className={clsx(styles.colorsTip, {
          [styles.fullScreen]: isFullScreenActive,
        })}
      >
        <ColorsTip />
      </Panel>
      {shouldShowLoader ? createPortal(
        <Loader
          isPageLoading
          className={styles.valueChainLoaderRoot}
          fullLoaderClassName={styles.fullLoader}
        />,
        document.body,
      ) : null }
      <CustomMarkerEnd color={VALUE_CHAIN_MAP_CONNECTION_COLORS.LOGICAL} id="edge-marker-logical" />
      <CustomMarkerEnd color={VALUE_CHAIN_MAP_CONNECTION_COLORS.SUPPLIERS_OF_GOODS} id="edge-marker-supplierOfGoods" />
      <CustomMarkerEnd color={VALUE_CHAIN_MAP_CONNECTION_COLORS.FINANCIAL} id="edge-marker-financial" />
      <CustomMarkerEnd color={VALUE_CHAIN_MAP_CONNECTION_COLORS.SERVICE} id="edge-marker-service" />
      <CustomMarkerEnd color={VALUE_CHAIN_MAP_CONNECTION_COLORS.MANUFACTURE} id="edge-marker-manufacture" />
      <div
        className={clsx(styles.reactFlowWrapper, {
          [styles.initialLayoutPending]: isInitialLayoutPending || isBoardContentHidden,
        })}
      >
        <ReactFlow
          className={clsx('reactFlow', styles.reactFlowCanvas)}
          nodes={nodes}
          edges={styledEdges}
          // ReactFlow owns viewport culling: it skips rendering off-screen nodes without
          // marking them hidden, so connections whose line crosses the viewport still draw.
          onlyRenderVisibleElements={!isDraftReviewMode && !isGroupingMode && isInitialViewportReady}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onMoveEnd={onMoveEnd}
          onEdgeClick={isGroupingMode ? noop : onEdgeClick}
          onEdgeDoubleClick={isGroupingMode ? noop : onEdgeDoubleClick}
          onConnect={isGroupingMode ? noop : onConnect}
          minZoom={REACT_FLOW.MIN_ZOOM}
          onNodeDragStart={isGroupingMode ? noop : onNodeDragStart}
          onNodeDragStop={isGroupingMode ? noop : onNodeDragStop}
          onNodeDrag={isGroupingMode ? noop : onNodeDrag}
          panOnScroll
          preventScrolling
          zoomOnDoubleClick={!isGroupingMode}
          defaultViewport={VALUE_CHAIN_MAP.DEFAULT_VIEWPORT}
          onDrop={isGroupingMode ? noop : onDrop}
          onDragOver={isGroupingMode ? noop : onDragOver}
          connectOnClick={!isAccessDisabled && !isScenarioViewMode && !isGroupingMode}
          nodesConnectable={!isAccessDisabled && !isScenarioViewMode && !isGroupingMode}
          nodesDraggable={
            !isAccessDisabled
            && !isDraftReviewMode
            && !openAICardPanel
            && !openScheduleConfigPanel
            && !openScheduleRunHistoryPanel
            && !openMonitoringResultsPanel
            && !isScenarioViewMode
            && !isGroupingMode
          }
          onNodeDoubleClick={!isAccessDisabled ? onNodeDoubleClick : noop}
        >
          <Background
            variant={BackgroundVariant.Cross}
            gap={60}
            color={!!activeViewScenarioId || !!activeViewResultId || isDraftReviewMode ? '#b0b1b2' : '#DBDDDE'}
            size={20}
            bgColor={!!activeViewScenarioId || !!activeViewResultId || isDraftReviewMode ? '#c2c3c3' : '#f3f4f4'}
          />
          {viewportController}
          <StickyColumnHeaders nodes={nodes} isFullScreenActive={isFullScreenActive} />
        </ReactFlow>
        {isDraftReviewMode ? (
          <div className={styles.draftReviewActions}>
            <Button
              title={isSupplierDiscoveryReview ? t('valueChainMap.discardResults') : t('common.cancel')}
              variant={BUTTON_VARIANT.cancel}
              onClick={onOpenDiscardDraftConfirm}
              className={clsx(styles.draftReviewActionButton, styles.draftReviewDiscardButton)}
            />
            <Button
              title={t('common.useResult')}
              variant={BUTTON_VARIANT.primary}
              onClick={onUseDraftResult}
              disabled={isUseDraftResultDisabled}
              className={clsx(styles.draftReviewActionButton, styles.draftReviewUseButton)}
            />
          </div>
        ) : null}
      </div>
      {selectedBoard ? (
        <RenameModal
          open={activeModal === VALUE_CHAIN_MAP_MODALS.RENAME}
          onSubmit={onRenameSubmit}
          onReject={closeModal}
          name={selectedBoard.name}
        />
      ) : null}
      <RenameModal
        onSubmit={onAddSwimlane}
        onReject={closeModal}
        open={activeModal === VALUE_CHAIN_MAP_MODALS.ADD_SWIMLANE}
        name=""
      />
      <DeleteModal
        open={isDeleteModalOpen}
        onReject={() => setDeleteModalOpen(false)}
        isLoading={false}
        name={selectedBoard?.name ? `${selectedBoard?.name}` : ''}
        title={DELETE_MODAL_TITLES.DELETE_SWIMLANE}
        onConfirm={onDeleteSwimlaneConfirm}
      />
      <DeleteModal
        open={isDraftDeleteModalOpen}
        onReject={onRejectDraftDelete}
        isLoading={isDraftDeleteLoading}
        name={draftCardToDeleteName || ''}
        onConfirm={onConfirmDraftDelete}
      />
      <DeleteModal
        open={isDiscardDraftConfirmOpen}
        onReject={onRejectDiscardDraftConfirm}
        isLoading={false}
        name=""
        title={DELETE_MODAL_TITLES.DISCARD_DRAFT_REVIEW}
        confirmTitle={t('common.discardChanges')}
        onConfirm={onConfirmDiscardDraft}
      />
      <DeleteRestrictedModal
        isOpen={activeModal === VALUE_CHAIN_MAP_MODALS.REFUSED_DELETE_SWIMLANE}
        onClose={closeModal}
        districtErrors={districtErrors}
      />
      <ConnectionVCModal
        isOpen={activeModal === VALUE_CHAIN_MAP_MODALS.VALUE_CONNECTION}
        onClose={closeModal}
        enumData={enumData}
        onAddServiceConnection={onAddServiceConnection}
        selectedEdge={selectedEdge}
      />
      {/* The side panels stay mounted and are shown by a flag, so each is declared to the
          assistant while its flag is on, under the same name its header shows. */}
      {openAIAgentsSidebar ? <AiSubScreen name={t('valueChainMap.aiAgentsSidebarTitle')} /> : null}
      {openAISessionsSidebar ? <AiSubScreen name={t('valueChainMap.aiSessionsTitle')} /> : null}
      {openMonitoringHubSidebar ? <AiSubScreen name={t(monitoringAgentTitleKey)} /> : null}
      {openAICardPanel ? (
        <AiSubScreen
          name={isMonitoringAgentKind(activeAgentKind) ? t(monitoringAgentTitleKey) : t('valueChainMap.aiPanelTitle')}
        />
      ) : null}
      {openScheduleConfigPanel ? (
        <AiSubScreen
          name={t(editingMonitoringSchedule
            ? 'valueChainMap.monitoringScheduleEditTitle'
            : 'valueChainMap.monitoringScheduleConfigTitle')}
        />
      ) : null}
      {openScheduleRunHistoryPanel ? <AiSubScreen name={t('valueChainMap.monitoringRunHistoryTitle')} /> : null}
      {openMonitoringResultsPanel ? (
        <AiSubScreen
          name={t('valueChainMap.monitoringResultsPanelTitle')}
          customData={{ sessionId: monitoringResultsSessionId, sessionTitle: monitoringResultsSessionTitle }}
        />
      ) : null}
      {openImpactScenariosSidebar ? <AiSubScreen name={t('valueChainMap.impactScenariosTitle')} /> : null}
      {selectedImpactAnalysis ? (
        <AiSubScreen
          name={t('valueChainMap.impactAnalysisPanelTitle')}
          customData={{ analysisId: selectedImpactAnalysis.id, name: selectedImpactAnalysis.name }}
        />
      ) : null}
      {openFilters ? <AiSubScreen name={t('common.filter')} /> : null}
      <AIAgentsSidebar
        open={openAIAgentsSidebar}
        onClose={onCloseAIAgentsSidebar}
        onSelectSupplierNTier={onOpenSupplierDiscoveryPanel}
        onSelectReputationMonitoring={onOpenReputationMonitoring}
        onSelectRegulationMonitoring={onOpenRegulationMonitoring}
        onSelectFinanceMonitoring={onOpenFinanceMonitoring}
        onSelectOwnershipMonitoring={onOpenOwnershipMonitoring}
        onSelectClimateMonitoring={onOpenClimateMonitoring}
        hasSupplierNTierAttention={hasSupplierNTierAttention}
        hasReputationMonitoringAttention={hasReputationMonitoringAttention}
        hasRegulationMonitoringAttention={hasRegulationMonitoringAttention}
        hasFinanceMonitoringAttention={hasFinanceMonitoringAttention}
        hasOwnershipMonitoringAttention={hasOwnershipMonitoringAttention}
        hasClimateMonitoringAttention={hasClimateMonitoringAttention}
      />
      <MonitoringAgentHubSidebar
        open={openMonitoringHubSidebar}
        titleKey={monitoringAgentTitleKey}
        agentDefinition={monitoringAgentDefinition}
        sessions={monitoringSessions}
        schedules={monitoringSchedules}
        activeTab={monitoringHubActiveTab}
        allAvailableCards={allAvailableAICards}
        valueChainMapId={monitoringResultsValueChainMapId}
        isSessionsLoading={isAISessionsLoading}
        isSessionsError={isAISessionsLoadFailed}
        isSchedulesLoading={isMonitoringSchedulesLoading}
        isSchedulesError={isMonitoringSchedulesLoadFailed}
        isScheduleActionLoading={isDeleteMonitoringScheduleLoading}
        isSessionActionLoading={isAISessionActionLoading}
        onBack={onBackToAIAgentsFromMonitoringHub}
        onClose={onCloseAIAgentsSidebar}
        onActiveTabChange={onMonitoringHubActiveTabChange}
        onNewSelection={onCreateMonitoringSessionSelection}
        onNewSchedule={onCreateMonitoringScheduleSelection}
        onOpenSchedule={onOpenMonitoringSchedule}
        onEditSchedule={onEditMonitoringSchedule}
        onDeleteSchedule={onDeleteMonitoringSchedule}
        onPauseSchedule={onPauseMonitoringSchedule}
        onResumeSchedule={onResumeMonitoringSchedule}
        onEditSession={onEditMonitoringSession}
        onDeleteSession={onDeleteMonitoringSession}
        onViewSessionResults={onViewMonitoringSessionResults}
      />
      <SessionsListSidebar
        open={openAISessionsSidebar}
        sessions={aiSessions}
        allAvailableCards={allAvailableAICards}
        isLoading={isAISessionsLoading}
        isError={isAISessionsLoadFailed}
        isActionLoading={isAISessionActionLoading}
        onBack={onBackToAIAgentsSidebar}
        onClose={onCloseAISessionsSidebar}
        onNewSelection={onCreateAISessionSelection}
        onEditSession={onEditAISession}
        onDeleteSession={onDeleteAISession}
      />
      <ImpactScenariosSidebar
        open={openImpactScenariosSidebar}
        onClose={onCloseImpactScenariosSidebar}
        scenarios={impactScenarios}
        isLoading={isImpactScenariosLoading}
        isCreating={isImpactScenarioCreating}
        isDeleting={isImpactScenariosDeleting}
        onCreateAnalysis={onCreateImpactScenario}
        onOpenAnalysis={onOpenImpactAnalysis}
        onDeleteAnalysis={onDeleteImpactScenario}
      />
      <AnalysisSidebar
        open={Boolean(selectedImpactAnalysis)}
        analysis={selectedImpactAnalysis}
        valueChainMapAnalysisById={valueChainMapAnalysisById}
        valueChainMapAnalysisResults={valueChainMapAnalysisResults}
        isAnalysisResultsLoading={isAnalysisResultsLoading}
        hasMoreAnalysisResults={hasMoreAnalysisResults}
        valueChainMapAnalysisScenarios={valueChainMapAnalysisScenarios}
        isAnalysisScenariosLoading={isAnalysisScenariosLoading}
        hasMoreAnalysisScenarios={hasMoreAnalysisScenarios}
        isCopyAnalysisResultLoading={isCopyAnalysisResultLoading}
        isCopyAnalysisScenariosLoading={isCopyAnalysisScenariosLoading}
        isDeleteAnalysisResultLoading={isDeleteAnalysisResultLoading}
        isStartImpactAnalysisLoading={isStartImpactAnalysisLoading}
        isUpdating={isImpactScenarioUpdating}
        isSavingScenario={isCreateScenarioSaving}
        boardEntities={boardEntities}
        onBack={onBackToImpactScenariosSidebar}
        onRenameAnalysis={onRenameImpactAnalysis}
        onStartImpactAnalysis={onStartImpactAnalysis}
        followedResultId={followedResultId}
        openRequestId={openRequestId}
        openResultId={openResultId}
        onSaveScenario={onSaveScenario}
        onUpdateScenario={onUpdateScenario}
        onDeleteAnalysisScenario={onDeleteAnalysisScenario}
        onLoadAnalysisResults={onLoadAnalysisResults}
        onLoadMoreAnalysisResults={onLoadMoreAnalysisResults}
        onLoadAnalysisScenarios={onLoadAnalysisScenarios}
        onLoadMoreAnalysisScenarios={onLoadMoreAnalysisScenarios}
        onCopyAnalysisResult={onCopyAnalysisResult}
        onCopyAnalysisScenarios={onCopyAnalysisScenarios}
        onDeleteAnalysisResult={onDeleteAnalysisResult}
        activeScenarioId={activeScenarioId}
        onSelectScenario={onSelectScenario}
        activeViewScenarioId={activeViewScenarioId}
        onViewScenario={onViewScenario}
        activeViewResultId={activeViewResultId}
        onViewResult={onViewResult}
        pendingScenarioInitialData={pendingScenarioInitialData}
        onClearPendingScenarioInitialData={onClearPendingScenarioInitialData}
        isAnalysisDetailsLoading={isAnalysisDetailsLoading}
      />
      <AICardPanel
        open={openAICardPanel}
        onClose={onCloseAICardPanel}
        onBack={isMonitoringAgentKind(activeAgentKind)
          ? onBackToMonitoringHubFromCardPanel
          : onBackToAISessionsSidebar}
        onAnalyzeBom={onAnalyzeBom}
        onSearchSuppliersSuccess={onSearchSuppliersSuccess}
        selectedCards={selectedAICards}
        allAvailableCards={allAvailableAICards}
        onSyncSelectedCardsFromSession={onSyncSelectedAICardsFromSession}
        onRemoveSelectedCard={onRemoveSelectedAICard}
        onToggleCard={onToggleSelectedAICard}
        onResetAll={onResetSelectedAICards}
        sessionId={isMonitoringAgentKind(activeAgentKind) && monitoringCardPanelFlow === 'new-schedule'
          ? undefined
          : sessionId}
        setSessionId={setSessionId}
        existingSession={aiCardPanelExistingSession}
        isVCAgentSessionsLoaded={isVCAgentSessionsLoaded}
        isVCAgentSessionsLoadFailed={isVCAgentSessionsLoadFailed}
        isAIDraftUnitsReadyForSelectionSync={isAIDraftUnitsReadyForSelectionSync}
        panelMode={isMonitoringAgentKind(activeAgentKind) ? 'monitoring' : 'n-tier'}
        monitoringFlow={monitoringCardPanelFlow}
        monitoringAgentDefinition={isMonitoringAgentKind(activeAgentKind)
          ? monitoringAgentDefinition
          : undefined}
        onStartMonitoring={onStartMonitoringSession}
        onContinueToSchedule={onContinueToSchedule}
        valueChainMapId={valueChainMapId || 0}
        onAgentConfigIdChange={onAgentConfigIdChange}
        onNTierSubTypeChange={onNTierSubTypeChange}
      />
      <ScheduleConfigPanel
        open={openScheduleConfigPanel}
        isSaving={isCreateMonitoringScheduleLoading}
        valueChainMapId={valueChainMapId || 0}
        initialAgentConfigId={monitoringAgentConfigId}
        scheduleToEdit={editingMonitoringSchedule}
        agentDefinition={isMonitoringAgentKind(activeAgentKind)
          ? monitoringAgentDefinition
          : undefined}
        onBack={onBackFromScheduleConfig}
        onClose={onCloseScheduleConfigPanel}
        onSave={onSaveMonitoringSchedule}
      />
      <AIGeneratingModal
        open={isAIGeneratingModalOpen}
        onClose={onCloseAIGeneratingModal}
        selectedCards={generatingAICards}
        session={(() => {
          if (isMonitoringGeneratingOverview) {
            return undefined;
          }
          if (isMonitoringAgentKind(activeAgentKind)) {
            return effectiveMonitoringSession;
          }
          return existingSession;
        })()}
        sessions={(() => {
          if (isMonitoringGeneratingOverview) {
            return generatingMonitoringSessions;
          }
          if (isMonitoringAgentKind(activeAgentKind)) {
            return [];
          }
          return generatingAISessions;
        })()}
        onSessionClick={onOpenGeneratingSession}
        lastBomSocketPayload={lastBomSocketPayload}
        titleKey={(() => {
          if (isMonitoringGeneratingOverview) {
            return 'valueChainMap.aiGeneratingMonitoringOverviewTitle';
          }
          if (isMonitoringAgentKind(activeAgentKind)) {
            return monitoringAgentDefinition.generatingLabelKey;
          }
          return undefined;
        })()}
        descriptionKey={(() => {
          if (isMonitoringGeneratingOverview) {
            return 'valueChainMap.aiGeneratingMonitoringOverviewDescription';
          }
          if (isMonitoringAgentKind(activeAgentKind)) {
            return monitoringAgentDefinition.generatingDescriptionKey;
          }
          return undefined;
        })()}
        useSessionLevelItemStatus={isMonitoringAgentKind(activeAgentKind) && !isMonitoringGeneratingOverview}
        sessionsListLayout={isMonitoringGeneratingOverview ? 'carousel' : 'list'}
      />
      <ScheduleRunHistoryPanel
        open={openScheduleRunHistoryPanel}
        schedule={activeMonitoringSchedule}
        sessionId={activeMonitoringSchedule ? undefined : monitoringResultsSessionId}
        sessionTitle={activeMonitoringSchedule ? undefined : monitoringResultsSessionTitle}
        highlightedSessionId={activeMonitoringSchedule ? undefined : monitoringResultsSessionId}
        initialExecutionKey={activeMonitoringSchedule ? undefined : monitoringResultsExecutionKey}
        valueChainMapId={monitoringResultsValueChainMapId}
        onBack={onBackFromScheduleRunHistory}
        onClose={onCloseScheduleRunHistoryPanel}
        onRunNow={activeMonitoringSchedule ? onRunMonitoringScheduleNow : undefined}
        isRunNowLoading={isRunMonitoringScheduleNowLoading}
      />
      <MonitoringResultsPanel
        open={openMonitoringResultsPanel}
        titleKey={monitoringAgentTitleKey}
        sessionTitle={monitoringResultsSessionTitle}
        valueChainMapId={monitoringResultsValueChainMapId}
        sessionId={monitoringResultsSessionId}
        runId={monitoringResultsRunId}
        agentDefinition={monitoringAgentDefinition}
        fallbackEntities={monitoringResultsFallbackEntities}
        breadcrumbMode={monitoringResultsBreadcrumbMode}
        breadcrumbScheduleTitle={monitoringResultsScheduleTitle}
        breadcrumbRunLabel={monitoringResultsRunLabel}
        showImpactScenarioCta={showMonitoringImpactScenarioCta}
        isImpactScenarioCreating={isMonitoringImpactScenarioCreating}
        onCreateImpactScenario={onCreateImpactScenarioFromMonitoringResults}
        onBack={onBackFromMonitoringResultsPanel}
        onClose={onCloseMonitoringResultsPanel}
      />
      <AICreatorBoMUnits
        key={`bom-units-${bomUnitsSession?.id || 'new'}`}
        open={openAICreatorBoMUnits}
        onClose={onCloseAICreatorBoMUnits}
        selectedCards={bomUnitsCards}
        availableManufacturerCards={availableBoMManufacturerCards}
        session={bomUnitsSession}
        onGenerateWithAI={onBoMGenerateWithAI}
        onAddRecord={onBoMAddRecord}
        onAddManufacturer={onBoMAddManufacturer}
        onConfirm={onBoMConfirm}
        onProcessingTabsChange={onBoMProcessingTabsChange}
        initialActiveTabNodeId={bomUnitsInitialTabNodeId}
        handleCloseModalBom={handleCloseModalBom}
        isBomSocketConnected={isBomSocketConnected}
        sendBomSocketUnitUpdated={sendBomSocketUnitUpdated}
        lastBomSocketPayload={lastBomSocketPayload}
        isSocketPollingFallbackActive={isSocketPollingFallbackActive}
      />
      <AICreatorSupplierUnits
        key={`supplier-units-${supplierUnitsSession?.id || 'new'}`}
        open={openAICreatorSupplierUnits}
        onClose={onCloseAICreatorSupplierUnits}
        selectedCards={supplierUnitsCards}
        session={supplierUnitsSession}
        onGenerateWithAI={onSupplierGenerateWithAI}
        onAddRecord={onSupplierAddRecord}
        onConfirm={onSupplierConfirm}
        onProcessingTabsChange={onSupplierProcessingTabsChange}
        initialActiveTabNodeId={supplierUnitsInitialTabNodeId}
        handleCloseModalSupplier={handleCloseModalSupplier}
        isSupplierSocketConnected={isBomSocketConnected}
        sendSupplierSocketUnitUpdated={sendBomSocketUnitUpdated}
        lastSupplierSocketPayload={lastBomSocketPayload}
        isSocketPollingFallbackActive={isSocketPollingFallbackActive}
        agentSessionsFulfilledTimeStamp={agentSessionsFulfilledTimeStamp}
        refetchAgentSessions={refetchAgentSessions}
      />
      <FiltersPanel
        open={openFilters}
        onClose={onOpenFilters}
        filtersData={filtersData}
        orgStructureData={orgStructureData}
        onApplyFilters={onApplyFilters}
        selectedCurrentFilters={selectedCurrentFilters}
        setSelectedCurrentFilters={setSelectedCurrentFilters}
        followConnections={followConnections}
        onToggleFollowConnections={onToggleFollowConnections}
      />
    </>
  );
}

export default ValueChainView;
